import qs from 'qs'
import oikosApi from '../services/oikos'
import { getRegs } from './fc'
import { TP_DOC_NF_DANFE, TP_DOC_NF_XML } from '../constants/oikosReceitas'

const NF_DOC_TYPES = [TP_DOC_NF_XML, TP_DOC_NF_DANFE]

const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** Mesmo critério de cargo do desfazer etapa do produto. */
function normalizeCargoText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Cargo sensível para ações financeiras destrutivas / revinculação.
 * @param {{ userCargo?: string, permId?: number|string }} opts
 */
export function canDesfazerFaturamentoReceita({ userCargo, permId } = {}) {
  if (Number(permId) === 1) return true
  const cargo = normalizeCargoText(userCargo)
  return (
    cargo.includes('desenvolvedor') ||
    cargo.includes('diretor') ||
    cargo.includes('gerente financeiro') ||
    cargo.includes('gerente tecnico')
  )
}

export function isRecFaturada(rec) {
  return Number(rec?.rec_fat) === 1 || num(rec?.nf_id) > 0
}

async function softDeleteDocumentoBundle({ documentoVincId, documentoId }) {
  const vincId = num(documentoVincId)
  const docId = num(documentoId)

  if (vincId) {
    await oikosApi.post('/', {
      action: 'update',
      table: 'documentoVinc',
      data: { documentoVinc_id: vincId, documentoVinc_ativo: 0 },
    })
  }

  if (!docId) return

  await oikosApi.post('/', {
    action: 'update',
    table: 'documento',
    data: { documento_id: docId, documento_ativo: 0 },
  })

  const arquivosRes = await oikosApi.get(
    `/?${qs.stringify({
      action: 'get',
      table: 'arquivo',
      data: {
        arquivo_tabRef: 'documento',
        arquivo_tabId: docId,
        arquivo_ativo: 1,
      },
      fields: { arquivo_id: 'arquivo_id' },
    })}`
  )

  for (const arq of arquivosRes?.data || []) {
    const arquivoId = num(arq?.arquivo_id)
    if (!arquivoId) continue
    await oikosApi.post('/', {
      action: 'update',
      table: 'arquivo',
      data: { arquivo_id: arquivoId, arquivo_ativo: 0 },
    })
  }
}

/**
 * Soft-delete XML (39) e PDF/DANFE (40) vinculados à NF.
 * @returns {Promise<object[]>} vínculos inativados
 */
export async function inativarDocsNf(nfId) {
  const tid = num(nfId)
  if (!tid) return []

  const vincRes = await oikosApi.get(
    `/?${qs.stringify({
      action: 'get',
      table: 'documentoVinc',
      data: {
        documentoVinc_tabRef: 'nf',
        documentoVinc_tabId: tid,
        documentoVinc_ativo: 1,
      },
      fields: {
        documentoVinc_id: 'documentoVinc_id',
        documento_id: 'documento_id',
        '(SELECT documento_ativo FROM documento WHERE documento.documento_id = documentoVinc.documento_id LIMIT 1)':
          'documento_ativo',
        '(SELECT tpDocumento_id FROM documento WHERE documento.documento_id = documentoVinc.documento_id LIMIT 1)':
          'tpDocumento_id',
      },
    })}`
  )

  const rows = (vincRes?.data || []).filter(
    (d) => Number(d?.documento_ativo) === 1 && NF_DOC_TYPES.includes(num(d?.tpDocumento_id))
  )

  const inactivated = []
  for (const d of rows) {
    await softDeleteDocumentoBundle({
      documentoVincId: d.documentoVinc_id,
      documentoId: d.documento_id,
    })
    inactivated.push(d)
  }
  return inactivated
}

async function countOutrosRecComNf(nfId, excludeRecId) {
  const nid = num(nfId)
  if (!nid) return 0
  const rows = await getRegs('rec', { nf_id: nid })
  let count = 0
  for (const r of rows || []) {
    if (num(r?.rec_id) === num(excludeRecId)) continue
    count += 1
  }
  return count
}

/**
 * Avalia se dá para desfazer o faturamento da receita.
 * @returns {{ ok: boolean, reason?: string, nfId?: number }}
 */
export function avaliarDesfazerFaturamento(rec) {
  const recId = num(rec?.rec_id)
  if (!recId) return { ok: false, reason: 'Receita inválida.' }
  if (!isRecFaturada(rec)) {
    return { ok: false, reason: 'Esta receita não está faturada.' }
  }
  return { ok: true, nfId: num(rec?.nf_id) || null }
}

/**
 * Desfaz o faturamento: desvincula NF da rec, zera rec_fat e soft-deleta XML/PDF
 * da NF quando nenhum outro rec ativo ainda a referencia.
 */
export async function desfazerFaturamentoReceita({ rec } = {}) {
  const avaliacao = avaliarDesfazerFaturamento(rec)
  if (!avaliacao.ok) {
    throw new Error(avaliacao.reason || 'Não é possível desfazer o faturamento.')
  }

  const recId = num(rec.rec_id)
  const nfId = num(rec.nf_id)

  await oikosApi.post('/', {
    action: 'update',
    table: 'rec',
    data: {
      rec_id: recId,
      nf_id: null,
      rec_fat: 0,
    },
  })

  let docsInativados = []
  let docsPuladosPorOutrosRec = false
  if (nfId > 0) {
    const outros = await countOutrosRecComNf(nfId, recId)
    if (outros === 0) {
      docsInativados = await inativarDocsNf(nfId)
    } else {
      docsPuladosPorOutrosRec = true
    }
  }

  return {
    recId,
    nfIdAnterior: nfId || null,
    docsInativados,
    docsPuladosPorOutrosRec,
    patch: { nf_id: null, rec_fat: 0 },
  }
}

/**
 * Lista NFs ativas para o seletor de vinculação.
 * @returns {Promise<Array<{ value: number, label: string, nf_comp?: string, raw: object }>>}
 */
export async function listarNfsAtivasParaVinculo({ limit = 400 } = {}) {
  const res = await oikosApi.get(
    `/?${qs.stringify({
      action: 'get',
      table: 'nf',
      data: { nf_ativo: 1 },
      order: 'nf_id DESC',
    })}`
  )
  const rows = Array.isArray(res?.data) ? res.data : []
  const sliced = rows.slice(0, Math.max(1, Number(limit) || 400))
  return sliced
    .map((n) => {
      const id = num(n?.nf_id)
      if (!id) return null
      const numNf = n?.nf_num != null && String(n.nf_num).trim() !== '' ? String(n.nf_num) : 's/n'
      const comp = n?.nf_comp ? ` · ${String(n.nf_comp).slice(0, 10)}` : ''
      return {
        value: id,
        label: `NF ${numNf} (id ${id})${comp}`,
        nf_comp: n?.nf_comp || null,
        raw: n,
      }
    })
    .filter(Boolean)
}

/**
 * Vincula uma NF já existente à receita e marca como faturada.
 */
export async function vincularNfExistente({ rec, nfId, nfComp } = {}) {
  const recId = num(rec?.rec_id)
  const nid = num(nfId)
  if (!recId) throw new Error('Receita inválida.')
  if (!nid) throw new Error('Selecione uma nota fiscal.')
  if (isRecFaturada(rec)) {
    throw new Error('Desfaça o faturamento atual antes de vincular outra NF.')
  }

  const nfRes = await oikosApi.get(
    `/?${qs.stringify({ action: 'get', table: 'nf', data: { nf_id: nid, nf_ativo: 1 } })}`
  )
  const nf = Array.isArray(nfRes?.data) ? nfRes.data[0] : null
  if (!nf) throw new Error(`NF ${nid} não encontrada ou inativa.`)

  const comp = nfComp || nf.nf_comp || null
  const data = {
    rec_id: recId,
    nf_id: nid,
    rec_fat: 1,
    ...(comp ? { rec_dtNfPrev: comp } : {}),
  }

  await oikosApi.post('/', {
    action: 'update',
    table: 'rec',
    data,
  })

  return {
    recId,
    nfId: nid,
    nf,
    patch: {
      nf_id: nid,
      rec_fat: 1,
      ...(comp ? { rec_dtNfPrev: comp } : {}),
    },
  }
}
